import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { fetchCourse, loc } from "@/lib/ethicsApi";
import { fetchTrackRecords } from "@/lib/academyProgress";
import { RubyShield, EthicsImage } from "./EthicsImage";
import { useSEO } from "@/lib/useSEO";
import { useT } from "@/i18n/LanguageContext";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { LockBadge } from "@/components/academy/TrialGate";
import { StatusBadge, PhaseProgress } from "@/components/academy/LessonNav";
import { LessonVirtueSeal } from "@/components/academy/LessonVirtueSeal";

export default function EthicsPhase() {
  const { phaseSlug } = useParams();
  const { lang, t } = useT();
  const [course, setCourse] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    fetchCourse().then(setCourse).catch(() => {});
    fetchTrackRecords("ethics").then(setRecords);
  }, [phaseSlug]);

  const phase = course?.phases?.find((p) => p.slug === phaseSlug);
  useSEO({
    title: phase ? `${loc(phase.title)} | Stoic Ethics | StoicOs.ai Academy` : "Stoic Ethics",
    description: phase ? loc(phase.centralQuestion) : "",
    canonical: `https://stoicos.ai/academy/ethics/phase/${phaseSlug}`,
    jsonLd: phase ? [{ "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Academy", item: "https://stoicos.ai/academy" },
        { "@type": "ListItem", position: 2, name: "Stoic Ethics", item: "https://stoicos.ai/academy/ethics" },
        { "@type": "ListItem", position: 3, name: loc(phase.title), item: `https://stoicos.ai/academy/ethics/phase/${phaseSlug}` },
      ] }] : [],
  });

  if (!course) return <div className="min-h-screen bg-ink" />;
  if (!phase) return <Navigate to="/academy/ethics" replace />;
  const media = course.media || {};

  return (
    <main className="min-h-screen bg-ink px-6 sm:px-10 py-24" data-testid="ethics-phase-page">
      <div className="max-w-4xl mx-auto">
        <DisciplineSwitcher active="ethics" />
        <nav aria-label="Breadcrumb" className="text-[10px] tracking-[0.25em] uppercase text-[#c86058] mb-10">
          <Link to="/academy" className="hover:text-gold">Academy</Link> / <Link to="/academy/ethics" className="hover:text-gold">Stoic Ethics</Link> / <span className="text-fog">{loc(phase.title, lang)}</span>
        </nav>
        <div className="flex items-center gap-4 mb-6">
          <RubyShield size={36} />
          <span className="font-serif text-3xl text-[#c86058]">{lang === "nl" ? "Fase" : "Phase"} {["I","II","III","IV","V","VI"][phase.order - 1]}</span>
          <span className="text-[10px] tracking-[0.25em] uppercase text-gold/70 border border-gold/30 px-2 py-1">{loc(phase.movement, lang)}</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-bone">{loc(phase.title, lang)}</h1>
        <p className="mt-6 font-serif italic text-xl text-gold/90 max-w-2xl">{loc(phase.centralQuestion, lang)}</p>
        <p className="mt-5 text-fog font-reading max-w-2xl leading-[1.9]" data-testid="ethics-phase-intro">{loc(phase.introduction, lang)}</p>
        <p className="mt-4 text-fog/80 font-reading max-w-2xl leading-[1.9]">{lang === "nl" ? "Kernvermogen: " : "Core ability: "}{loc(phase.coreAbility, lang)}</p>
        <p className="mt-3"><PhaseProgress phase={phase} records={records} accent="#c86058" /></p>

        <div className="mt-14 space-y-5">
          {phase.lessons.map((l) => {
            const rec = records.find((r) => r.lessonSlug === l.slug);
            return (
              <div key={l.slug} className="border border-[#c8a95a]/20 bg-[#14211a] hover:border-gold/50 transition-colors p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#c86058]">{lang === "nl" ? "Les" : "Lesson"} {l.order} · {l.estimatedMinutes} min</p>
                    <p className="font-serif text-xl text-bone mt-1">{loc(l.title, lang)}</p>
                  </div>
                  <StatusBadge rec={rec} accent="#c86058" />
                </div>
                {course.access === "preview" ? (
                  <div className="mt-4">
                    <LockBadge className="mb-2 block" />
                    <Link to="/my-stoicos" data-testid={`ethics-lesson-trial-${l.slug}`}
                      className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 text-[11px] font-semibold tracking-[0.2em] uppercase bg-[#6b2a2a] text-[#f5e4e4] hover:brightness-125 active:brightness-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c86058]">
                      {t("trial.cta")} <span aria-hidden>→</span>
                    </Link>
                  </div>
                ) : (<>
                  <Link to={`/academy/ethics/phase/${phase.slug}/lesson/${l.slug}`} data-testid={`ethics-lesson-link-${l.slug}`}
                    className="mt-4 inline-flex items-center justify-center gap-2 min-h-[48px] px-5 text-[11px] font-semibold tracking-[0.2em] uppercase bg-[#6b2a2a] text-[#f5e4e4] hover:brightness-125 active:brightness-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c86058]">
                    {t("academy.nav.openLesson")} <span aria-hidden>→</span>
                  </Link>
                  {l.locked && <LockBadge className="mt-2 block" />}
                </>)}
                <p className="text-sm text-fog/70 mt-3 font-reading italic">{loc(l.subtitle, lang)}</p>
              </div>
            );
          })}
        </div>

        {phase.imageSlotId && (
          <div className="mt-16 max-w-xl"><EthicsImage media={media} assetKey={phase.imageSlotId} alt="" /></div>
        )}
        <LessonVirtueSeal />
      </div>
    </main>
  );
}
